import { addScales } from "@/components/PddContextProvider/DiceCanvases/helpers/addScales";
import {
  DiceActionTypes,
  DiceRollActionTypes,
  getDiceServiceUrl,
  getMediaUrl,
  isDebug,
  WorkerPayloadTypes,
} from "@/constants";
import { Context } from "@/context";
import { playSound } from "@/helpers/playSound";
import { preloadAssets } from "@/helpers/preloadAssets";
import { useDicePreviewerStore } from "@/hooks/useDicePreviewerStore";
import { useDiceSets } from "@/hooks/useDiceSets";
import { useUserDiceDataStore } from "@/hooks/useUserDiceDataStore";
import { key } from "@dndbeyond/message-broker-lib/dist/lib/constants";
import { isEmpty } from "lodash";
import { useContext, useEffect, useRef } from "react";

export const useRenderedWorkerListeners = () => {
  const {
    nodeEnv,
    env,
    user,
    renderedWorker,
    physicsWorker,
    setIsRenderedWorkerReady,
    setHasRenderedWorkerErrored,
    logger,
    errorCallback,
    toastCallback,
    manifests,
  } = useContext(Context);
  const diceServiceUrl = getDiceServiceUrl(String(nodeEnv), String(env));
  const customUrlMap = useDicePreviewerStore((state) => state.customUrlMap);
  const mediaUrl = getMediaUrl(String(nodeEnv), String(env));
  const settings = useUserDiceDataStore(
    (state) => state.getUserDiceData(Number(user?.id))?.settings,
  );
  const volume = settings?.volume !== undefined ? settings?.volume : 1;
  const shadowQuality = settings?.shadowQuality || 3;
  const activeSources = useRef<Set<string>>(new Set());
  const diceSets = useDiceSets(nodeEnv, env);

  useEffect(() => {
    if (isEmpty(manifests) || !physicsWorker || !renderedWorker) return;

    if (isDebug) {
      console.log("Setting up renderedWorker listeners");
    }

    const handleRenderedMessage = ({
      data: { type, payload },
    }: MessageEvent) => {
      if (isDebug && type !== WorkerPayloadTypes.WorkerStats) {
        console.log(
          "[DiceCanvases] Message from renderedWorker:",
          type,
          payload,
        );
      }

      if (type === WorkerPayloadTypes.ComponentMounted) {
        renderedWorker.postMessage({
          type: WorkerPayloadTypes.Manifests,
          payload: manifests,
        });

        if (isDebug) {
          console.log(
            "[DiceCanvases] Sent manifests to renderedWorker:",
            manifests,
          );
        }

        renderedWorker.postMessage({
          type: WorkerPayloadTypes.ShadowQuality,
          payload: shadowQuality,
        });

        if (isDebug) {
          console.log(
            "[DiceCanvases] Sent shadow quality to renderedWorker:",
            shadowQuality,
          );
        }

        renderedWorker.postMessage({
          type: WorkerPayloadTypes.DiceSets,
          payload: diceSets,
        });

        if (isDebug) {
          console.log(
            "[DiceCanvases] Sent dice sets to renderedWorker:",
            diceSets,
          );
        }

        setIsRenderedWorkerReady?.(true);

        if (isDebug) {
          console.log("renderedWorker is ready");
        }
      } else if (type === DiceRollActionTypes.Fulfilled) {
        window[key]?.dispatch(payload);
      } else if (type === WorkerPayloadTypes.PlaySound) {
        if (!payload.isCollision && activeSources.current.has(payload.url))
          return;

        activeSources.current.add(payload.url);
        playSound(
          payload.url,
          payload.volume * (volume ?? 1),
          activeSources.current,
        );
      } else if (type === WorkerPayloadTypes.RemoveRoll) {
        physicsWorker.postMessage({
          type: WorkerPayloadTypes.RemoveRoll,
          payload,
        });
      } else if (type === DiceActionTypes.SetUpdate) {
        preloadAssets(mediaUrl, [payload.manifest], customUrlMap);
        addScales({ [payload.setId]: payload.manifest }, physicsWorker);
      } else if (type === WorkerPayloadTypes.ToastMessage) {
        toastCallback?.(payload);
      }
    };
    const handleError = (error: ErrorEvent) => {
      // clean up message handler if error happens before component mounts
      renderedWorker.removeEventListener("message", handleRenderedMessage);

      if (isDebug) {
        logger?.error("renderedWorker error", {
          exception: JSON.stringify(error.message),
        });
      }

      renderedWorker.postMessage({
        type: WorkerPayloadTypes.RemoveEventListener,
      });
      errorCallback?.(
        "renderedWorker encountered an error. Falling back to instant dice.",
      );
      setHasRenderedWorkerErrored?.(true);
    };

    renderedWorker.addEventListener("error", handleError);
    renderedWorker.addEventListener("message", handleRenderedMessage);

    return () => {
      renderedWorker.removeEventListener("error", handleError);
      renderedWorker.removeEventListener("message", handleRenderedMessage);
    };
  }, [
    customUrlMap,
    diceServiceUrl,
    diceSets,
    errorCallback,
    logger,
    manifests,
    mediaUrl,
    physicsWorker,
    renderedWorker,
    setHasRenderedWorkerErrored,
    setIsRenderedWorkerReady,
    shadowQuality,
    toastCallback,
    volume,
  ]);
};
